<template>
  <PublicLayout :page-title="post ? post.title : 'Artículo'">
    <!-- Loading State -->
    <div v-if="loading" class="animate-pulse">
      <div class="py-16 bg-gradient-to-br from-slate-50 to-white">
        <div class="max-w-4xl mx-auto px-6 text-center">
          <div class="h-8 bg-slate-200 rounded-full w-32 mx-auto mb-6" />
          <div class="h-12 bg-slate-200 rounded-xl w-3/4 mx-auto mb-4" />
          <div class="h-6 bg-slate-200 rounded w-1/2 mx-auto" />
        </div>
      </div>
      <div class="max-w-5xl mx-auto px-6 -mt-8">
        <div class="h-80 bg-slate-200 rounded-3xl" />
      </div>
      <div class="max-w-3xl mx-auto px-6 py-16 space-y-4">
        <div class="h-4 bg-slate-200 rounded w-full" />
        <div class="h-4 bg-slate-200 rounded w-5/6" />
        <div class="h-4 bg-slate-200 rounded w-4/6" />
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="max-w-3xl mx-auto px-6 py-16">
      <div class="bg-red-50 border border-red-200 rounded-2xl p-6">
        <div class="flex">
          <ExclamationTriangleIcon class="w-5 h-5 text-red-400 mr-3 mt-0.5" />
          <div>
            <h3 class="text-sm font-medium text-red-800">Error al cargar el artículo</h3>
            <p class="text-sm text-red-700 mt-1">{{ error }}</p>
            <button
              @click="loadPost"
              class="mt-3 text-sm font-medium text-red-800 hover:text-red-900 underline"
            >
              Intentar de nuevo
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Post Content -->
    <article v-else-if="post">
      <!-- Hero Header -->
      <header class="relative py-24 bg-gradient-to-br from-slate-50 to-white overflow-hidden">
        <div class="absolute top-0 right-0 w-96 h-96 bg-accent-200/10 rounded-full blur-3xl" />

        <div class="relative max-w-4xl mx-auto px-6 text-center">
          <!-- Category Badge -->
          <RouterLink
            v-if="post.category"
            :to="`/blog/categoria/${post.category.slug}`"
            class="inline-flex items-center gap-2 px-4 py-2 bg-accent-50 border border-accent-200 rounded-full text-accent-700 text-sm font-medium mb-6 hover:bg-accent-100 transition-colors animate-fade-up"
          >
            {{ post.category.name }}
          </RouterLink>

          <!-- Title -->
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary tracking-tight mb-6 leading-tight animate-fade-up delay-100">
            {{ post.title }}
          </h1>

          <!-- Meta -->
          <div class="flex flex-wrap items-center justify-center gap-4 text-slate-500 animate-fade-up delay-200">
            <!-- Author -->
            <div v-if="post.author" class="flex items-center gap-2">
              <img
                v-if="post.author.avatar"
                :src="post.author.avatar.url"
                :alt="post.author.name"
                class="w-10 h-10 rounded-full object-cover"
              />
              <div
                v-else
                class="w-10 h-10 rounded-full bg-gradient-to-br from-accent-500 to-teal-500 flex items-center justify-center text-white font-medium text-sm"
              >
                {{ getAuthorInitials(post.author.name) }}
              </div>
              <span class="font-medium text-text-primary">{{ post.author.name }}</span>
            </div>
            <span class="w-1 h-1 bg-slate-300 rounded-full" />
            <time class="flex items-center gap-1">
              <CalendarIcon class="w-4 h-4" />
              {{ formatDate(post.publishedAt) }}
            </time>
            <span class="w-1 h-1 bg-slate-300 rounded-full" />
            <span class="flex items-center gap-1">
              <ClockIcon class="w-4 h-4" />
              {{ readingTime }} min lectura
            </span>
          </div>
        </div>
      </header>

      <!-- Featured Image -->
      <div v-if="post.featuredImage" class="max-w-5xl mx-auto px-6 -mt-8 animate-fade-up delay-300">
        <div class="rounded-3xl overflow-hidden shadow-2xl">
          <img
            :src="post.featuredImage.url"
            :alt="post.featuredImage.alt || post.title"
            class="w-full"
          />
        </div>
      </div>

      <!-- Content -->
      <div class="max-w-3xl mx-auto px-6 py-16">
        <PostContent
          :content="post.content"
          :show-toc="false"
          @toc-updated="handleTocUpdate"
        />
      </div>

      <!-- Tags -->
      <div v-if="post.tags && post.tags.length" class="max-w-3xl mx-auto px-6 pb-12">
        <div class="flex flex-wrap gap-2">
          <button
            v-for="tag in post.tags"
            :key="tag"
            @click="handleTagClick(tag)"
            class="px-4 py-2 bg-slate-100 text-slate-700 rounded-full hover:bg-accent-100 hover:text-accent-700 transition-colors"
          >
            #{{ tag }}
          </button>
        </div>
      </div>

      <!-- Author Bio -->
      <div v-if="post.author" class="max-w-3xl mx-auto px-6 pb-16">
        <div class="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
          <div class="flex items-start gap-6">
            <img
              v-if="post.author.avatar"
              :src="post.author.avatar.url"
              :alt="post.author.name"
              class="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
            />
            <div
              v-else
              class="w-20 h-20 rounded-2xl bg-gradient-to-br from-accent-500 to-teal-500 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0"
            >
              {{ getAuthorInitials(post.author.name) }}
            </div>
            <div>
              <h3 class="text-xl font-bold text-text-primary mb-1">{{ post.author.name }}</h3>
              <p v-if="post.author.role" class="text-accent-600 text-sm mb-3">{{ post.author.role }}</p>
              <p v-if="post.author.bio" class="text-text-secondary">{{ post.author.bio }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Share Section -->
      <div class="max-w-3xl mx-auto px-6 pb-16">
        <div class="bg-accent-50 rounded-2xl p-6 border border-accent-100">
          <div class="flex items-center justify-between flex-wrap gap-4">
            <h3 class="text-lg font-semibold text-text-primary">
              ¿Te resultó útil este artículo?
            </h3>
            <div class="flex items-center gap-3">
              <span class="text-sm text-text-secondary">Compártelo:</span>
              <!-- Twitter -->
              <a
                :href="twitterShareUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="p-2.5 bg-white text-slate-500 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-colors shadow-sm"
                title="Compartir en Twitter"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <!-- LinkedIn -->
              <a
                :href="linkedinShareUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="p-2.5 bg-white text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors shadow-sm"
                title="Compartir en LinkedIn"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clip-rule="evenodd" />
                </svg>
              </a>
              <!-- WhatsApp -->
              <a
                :href="whatsappShareUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="p-2.5 bg-white text-slate-500 hover:text-green-500 hover:bg-green-50 rounded-xl transition-colors shadow-sm"
                title="Compartir en WhatsApp"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.338 3.662C14.56 1.885 12.224 1 9.888 1 4.724 1 .55 5.174.55 10.338c0 1.632.427 3.226 1.24 4.627L.55 19l4.125-1.082c1.35.737 2.87 1.125 4.426 1.125h.004C14.276 19.043 18.45 14.87 18.45 9.705c0-2.336-.885-4.672-2.662-6.449l.55.406zM9.888 17.788c-1.388 0-2.748-.371-3.944-1.073l-.283-.168-2.937.77.784-2.867-.185-.295A7.298 7.298 0 012.806 10.338C2.806 6.418 6.01 3.214 9.93 3.214c1.906 0 3.697.742 5.046 2.09 1.348 1.348 2.09 3.14 2.09 5.046-.042 3.92-3.246 7.124-7.166 7.124l-.012.314z" clip-rule="evenodd" />
                  <path d="M13.273 11.273c-.196-.099-1.162-.573-1.342-.638-.18-.065-.311-.099-.442.098-.131.196-.508.639-.623.77-.115.131-.23.147-.426.049-.197-.099-.832-.307-1.585-.978-.586-.523-.983-1.17-1.098-1.366-.114-.196-.012-.302.086-.4.088-.088.196-.23.295-.344.098-.115.131-.196.196-.327.065-.131.033-.245-.016-.344-.05-.098-.442-1.066-.606-1.459-.16-.382-.323-.33-.442-.337-.115-.007-.246-.007-.377-.007s-.344.049-.525.245c-.18.196-.688.672-.688 1.64 0 .967.705 1.902.803 2.033.099.131 1.394 2.13 3.377 2.988.472.204.84.326 1.127.417.474.151.905.13 1.246.078.38-.057 1.163-.475 1.327-.933.164-.458.164-.852.115-.933-.049-.082-.18-.131-.377-.23z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Related Posts -->
      <section v-if="relatedPosts.length" class="bg-slate-50/50 py-16">
        <div class="max-w-6xl mx-auto px-6">
          <h2 class="text-3xl font-bold text-text-primary text-center mb-12">
            Artículos relacionados
          </h2>
          <div class="grid md:grid-cols-3 gap-8">
            <BlogCard
              v-for="relatedPost in relatedPosts.slice(0, 3)"
              :key="relatedPost.id"
              :post="relatedPost"
            />
          </div>
        </div>
      </section>

      <!-- Floating TOC -->
      <FloatingTOC
        :toc-items="tocItems"
        :active-heading="activeHeading"
        @heading-click="scrollToHeading"
      />
    </article>

    <!-- Not Found State -->
    <div v-else class="max-w-3xl mx-auto px-6 py-20 text-center">
      <div class="w-20 h-20 mx-auto mb-6 bg-slate-100 rounded-2xl flex items-center justify-center">
        <DocumentTextIcon class="w-10 h-10 text-slate-400" />
      </div>
      <h2 class="text-2xl font-bold text-text-primary mb-2">Artículo no encontrado</h2>
      <p class="text-text-secondary mb-8">El artículo que buscas no existe o ha sido eliminado.</p>
      <RouterLink
        to="/blog"
        class="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent-600 to-teal-500 text-white font-semibold rounded-xl shadow-lg shadow-accent-500/25 hover:shadow-xl hover:scale-[1.02] transition-all"
      >
        <ArrowLeftIcon class="w-4 h-4" />
        Volver al blog
      </RouterLink>
    </div>
  </PublicLayout>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { useSeo } from '@/composables/useSeo.js'
import { BASE_URL } from '@/constants/seo'
import { useSchemaOrg, createBlogPostingSchema } from '@/composables/useSchemaOrg'
import { signalPrerenderReady } from '@/composables/usePrerenderReady'
import PublicLayout from '@/components/layout/PublicLayout.vue'
import PostContent from '@/components/blog/PostContent.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import FloatingTOC from '@/components/blog/FloatingTOC.vue'
import blogService from '@/services/blogService'
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CalendarIcon,
  ClockIcon,
  ArrowLeftIcon
} from '@heroicons/vue/24/outline'

// Router
const route = useRoute()
const router = useRouter()

// State
const loading = ref(false)
const error = ref(null)
const post = ref(null)
const relatedPosts = ref([])
const tocItems = ref([])
const activeHeading = ref('')

// SEO - Reactive computed values
const seoTitle = computed(() => {
  if (!post.value) return 'Artículo - Blog Olvidadme'
  return post.value.seo?.metaTitle || `${post.value.title} - Blog Olvidadme`
})

const seoDescription = computed(() => {
  if (!post.value) return 'Lee nuestros artículos especializados sobre derecho al olvido y privacidad digital'
  return post.value.excerpt || post.value.metaDescription || `Lee más sobre ${post.value.title} en nuestro blog especializado en derecho al olvido`
})

const seoImage = computed(() => {
  const url = post.value?.featuredImage?.url
  // Sin featured image devolvemos null para que useSeo aplique DEFAULT_IMAGE (absoluta).
  // Los scrapers de Open Graph requieren URLs absolutas.
  if (!url) return null
  return url.startsWith('http') ? url : `${BASE_URL}${url}`
})

const seoArticle = computed(() => {
  if (!post.value) return null
  const author = post.value.author
  return {
    publishedAt: post.value.publishedAt,
    updatedAt: post.value.updatedAt,
    author: author
      ? [author.firstName, author.lastName].filter(Boolean).join(' ')
      : undefined
  }
})

const seoCanonical = computed(() => {
  if (!post.value) return '/blog'
  return `/blog/${post.value.slug}`
})

useSeo({
  title: seoTitle,
  description: seoDescription,
  canonical: seoCanonical,
  image: seoImage,
  type: 'article',
  article: seoArticle
})

// Schema.org BlogPosting (SEO Phase 4)
const blogSchema = computed(() => {
  if (!post.value) return null
  return createBlogPostingSchema({
    ...post.value,
    wordCount: post.value.content
      ? post.value.content.replace(/<[^>]*>/g, '').split(/\s+/).length
      : 0
  })
})
useSchemaOrg(blogSchema)

// Computed
const readingTime = computed(() => {
  if (!post.value?.content) return 1
  const wordsPerMinute = 200
  const textContent = post.value.content.replace(/<[^>]*>/g, '').trim()
  const wordCount = textContent.split(/\s+/).length
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute))
})

const currentUrl = computed(() => window.location.href)

const twitterShareUrl = computed(() => {
  if (!post.value) return ''
  const text = `${post.value.title} - Olvidadme Blog`
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(currentUrl.value)}`
})

const linkedinShareUrl = computed(() => {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl.value)}`
})

const whatsappShareUrl = computed(() => {
  if (!post.value) return ''
  const text = `${post.value.title} - ${currentUrl.value}`
  return `https://wa.me/?text=${encodeURIComponent(text)}`
})

// Methods
const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const getAuthorInitials = (name) => {
  if (!name) return 'A'
  const words = name.split(' ')
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase()
}

const loadPost = async () => {
  try {
    loading.value = true
    error.value = null

    const slug = route.params.slug
    const response = await blogService.getPost(slug, true)
    post.value = response.post

    if (post.value && post.value._id) {
      blogService.incrementViews(post.value._id).catch(() => {})
    }

    await loadRelatedPosts()
  } catch (err) {
    console.error('Error loading post:', err)
    error.value = err.response?.status === 404
      ? 'El artículo no fue encontrado'
      : 'Error al cargar el artículo'
  } finally {
    loading.value = false
    nextTick(() => signalPrerenderReady())
  }
}

const loadRelatedPosts = async () => {
  if (!post.value) return

  try {
    const params = {
      limit: 6,
      status: 'published'
    }

    if (post.value.category) {
      params.category = post.value.category.id
    }

    const response = await blogService.getPosts(params)
    relatedPosts.value = (response.posts || [])
      .filter(p => p.id !== post.value.id)
      .slice(0, 3)
  } catch (err) {
    console.error('Error loading related posts:', err)
  }
}

const handleTocUpdate = (items) => {
  tocItems.value = items
  nextTick(() => {
    setupScrollSpy()
  })
}

const scrollToHeading = (id) => {
  const element = document.getElementById(id)
  if (element) {
    const offset = 100
    const elementPosition = element.offsetTop - offset

    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth'
    })

    window.history.replaceState(null, null, `#${id}`)
    activeHeading.value = id
  }
}

const handleTagClick = (tag) => {
  router.push({
    name: 'BlogIndex',
    query: { search: `#${tag}` }
  })
}

const setupScrollSpy = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activeHeading.value = entry.target.id
        }
      })
    },
    {
      rootMargin: '-100px 0px -50% 0px',
      threshold: 0.1
    }
  )

  tocItems.value.forEach((item) => {
    const element = document.getElementById(item.id)
    if (element) {
      observer.observe(element)
    }
  })

  if (!window.headingObserver) {
    window.headingObserver = observer
  }
}

const cleanupScrollSpy = () => {
  if (window.headingObserver) {
    window.headingObserver.disconnect()
    window.headingObserver = null
  }
}

// Lifecycle
onMounted(async () => {
  await loadPost()

  if (route.hash) {
    nextTick(() => {
      const id = route.hash.slice(1)
      const element = document.getElementById(id)
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' })
          activeHeading.value = id
        }, 500)
      }
    })
  }
})

onUnmounted(() => {
  cleanupScrollSpy()
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Loading animation */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Focus states */
a:focus,
button:focus {
  outline: 2px solid theme('colors.accent.500');
  outline-offset: 2px;
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .animate-fade-up {
    animation: none;
  }

  html {
    scroll-behavior: auto;
  }
}
</style>
